import React, { useState, useEffect } from 'react';
import { Check, X, ArrowUp, ArrowDown, Minus, Clock, AlertCircle, Loader2 } from 'lucide-react';
import { getAuthHeaders } from '@/services/authConfig';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

interface CheckpointData {
  id: string;
  run_id: string;
  iteration: number;
  status: string;
  best_config: Record<string, unknown>;
  pareto_snapshot: Array<Record<string, unknown>>;
  improvement_from_baseline: Record<string, number>;
  improvement_from_last: Record<string, number>;
  config_diff?: {
    added: Record<string, unknown>;
    removed: Record<string, unknown>;
    changed: Record<string, { old: unknown; new: unknown }>;
  };
  publish_on_approve: boolean;
  reviewed_by?: string;
  reviewed_at?: string;
  review_notes?: string;
  created_at?: string;
  expires_at?: string;
}

interface OptimizationStatus {
  run_id: string;
  status: string;
  current_iteration: number;
  max_iterations: number;
  total_eval_runs: number;
  max_eval_runs: number;
  baseline_values: Record<string, number>;
  current_best_values: Record<string, number>;
  exploration_mode: string;
}

interface CheckpointReviewProps {
  evalId: string;
  checkpointId: string;
  open: boolean;
  onClose: () => void;
  onAction?: (action: 'approved' | 'rejected') => void;
}

export function CheckpointReview({ evalId, checkpointId, open, onClose, onAction }: CheckpointReviewProps) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkpoint, setCheckpoint] = useState<CheckpointData | null>(null);
  const [status, setStatus] = useState<OptimizationStatus | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [publishOnApprove, setPublishOnApprove] = useState(true);
  const [stopOnReject, setStopOnReject] = useState(false);
  const [feedback, setFeedback] = useState('');

  useEffect(() => {
    if (open && checkpointId) {
      fetchData();
    }
  }, [open, checkpointId, evalId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [checkpointRes, statusRes] = await Promise.all([
        fetch(`/api/evals/${evalId}/checkpoints/${checkpointId}`, {
          headers: getAuthHeaders(),
          credentials: 'include',
        }),
        fetch(`/api/evals/${evalId}/optimization/status`, {
          headers: getAuthHeaders(),
          credentials: 'include',
        }),
      ]);

      if (!checkpointRes.ok) {
        throw new Error('Failed to load checkpoint');
      }

      const checkpointData = await checkpointRes.json();
      setCheckpoint(checkpointData);
      setPublishOnApprove(checkpointData.publish_on_approve ?? true);

      if (statusRes.ok) {
        setStatus(await statusRes.json());
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      setSubmitting(true);
      setError(null);

      const res = await fetch(`/api/evals/${evalId}/checkpoints/${checkpointId}/approve`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          notes: reviewNotes,
          publish: publishOnApprove,
          feedback: feedback.trim() || undefined,
        }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to approve checkpoint');
      }

      onAction?.('approved');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    try {
      setSubmitting(true);
      setError(null);

      const res = await fetch(`/api/evals/${evalId}/checkpoints/${checkpointId}/reject`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          notes: reviewNotes,
          stop_run: stopOnReject,
        }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to reject checkpoint');
      }

      onAction?.('rejected');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject');
    } finally {
      setSubmitting(false);
    }
  };

  const formatValue = (value: unknown): string => {
    if (typeof value === 'number') {
      return value.toFixed(4);
    }
    if (typeof value === 'string') {
      return value.length > 100 ? value.substring(0, 100) + '...' : value;
    }
    return JSON.stringify(value);
  };

  const renderImprovement = (value: number, minimize = false) => {
    const improved = minimize ? value < 0 : value > 0;
    const neutral = Math.abs(value) < 0.001;

    if (neutral) {
      return (
        <span className="flex items-center text-muted-foreground">
          <Minus className="h-4 w-4 mr-1" />
          {value.toFixed(4)}
        </span>
      );
    }

    return (
      <span className={cn(
        "flex items-center",
        improved ? "text-green-600" : "text-red-600"
      )}>
        {improved ? (
          <ArrowUp className="h-4 w-4 mr-1" />
        ) : (
          <ArrowDown className="h-4 w-4 mr-1" />
        )}
        {value > 0 ? '+' : ''}{value.toFixed(4)}
      </span>
    );
  };

  const getExpirationStatus = () => {
    if (!checkpoint?.expires_at) return null;

    const expires = new Date(checkpoint.expires_at);
    const now = new Date();
    const hoursRemaining = (expires.getTime() - now.getTime()) / (1000 * 60 * 60);

    if (hoursRemaining < 0) {
      return { label: 'Expired', variant: 'destructive' as const };
    }
    if (hoursRemaining < 2) {
      return { label: `${Math.round(hoursRemaining * 60)} min left`, variant: 'destructive' as const };
    }
    if (hoursRemaining < 12) {
      return { label: `${Math.round(hoursRemaining)} hours left`, variant: 'secondary' as const };
    }
    return { label: `${Math.round(hoursRemaining)} hours left`, variant: 'outline' as const };
  };

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={isOpen => !isOpen && onClose()}>
        <DialogContent className="sm:max-w-[700px]">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!checkpoint) {
    return (
      <Dialog open={open} onOpenChange={isOpen => !isOpen && onClose()}>
        <DialogContent className="sm:max-w-[700px]">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>Checkpoint not found</AlertDescription>
          </Alert>
        </DialogContent>
      </Dialog>
    );
  }

  const expirationStatus = getExpirationStatus();
  const isPending = checkpoint.status === 'pending';

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>Checkpoint Review - Iteration {checkpoint.iteration}</DialogTitle>
            <div className="flex items-center gap-2">
              {expirationStatus && (
                <Badge variant={expirationStatus.variant}>
                  <Clock className="h-3 w-3 mr-1" />
                  {expirationStatus.label}
                </Badge>
              )}
              <Badge variant={isPending ? 'default' : 'secondary'}>
                {checkpoint.status}
              </Badge>
            </div>
          </div>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Progress */}
        {status && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Progress</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>Iteration {status.current_iteration} of {status.max_iterations}</span>
                <span>{status.exploration_mode} mode</span>
              </div>
              <Progress
                value={(status.current_iteration / status.max_iterations) * 100}
              />
            </CardContent>
          </Card>
        )}

        {/* Improvements */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Improvements</CardTitle>
            <CardDescription>Changes from baseline and last checkpoint</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div className="font-medium">Objective</div>
              <div className="font-medium text-center">vs Baseline</div>
              <div className="font-medium text-center">vs Last</div>

              {Object.keys(checkpoint.improvement_from_baseline).map(key => (
                <React.Fragment key={key}>
                  <div>{key}</div>
                  <div className="text-center">
                    {renderImprovement(checkpoint.improvement_from_baseline[key])}
                  </div>
                  <div className="text-center">
                    {checkpoint.improvement_from_last[key] !== undefined
                      ? renderImprovement(checkpoint.improvement_from_last[key])
                      : <span className="text-muted-foreground">—</span>
                    }
                  </div>
                </React.Fragment>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Config Diff */}
        {checkpoint.config_diff && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Configuration Changes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {Object.keys(checkpoint.config_diff.changed).length > 0 && (
                <div className="space-y-2">
                  {Object.entries(checkpoint.config_diff.changed).map(([key, change]) => (
                    <div key={key} className="text-sm font-mono bg-muted p-2 rounded">
                      <div className="text-muted-foreground">{key}:</div>
                      <div className="text-red-600 line-through">
                        - {formatValue(change.old)}
                      </div>
                      <div className="text-green-600">
                        + {formatValue(change.new)}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {Object.keys(checkpoint.config_diff.added).length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Added</Label>
                  {Object.entries(checkpoint.config_diff.added).map(([key, value]) => (
                    <div key={key} className="text-sm font-mono text-green-600 bg-muted p-2 rounded">
                      + {key}: {formatValue(value)}
                    </div>
                  ))}
                </div>
              )}

              {Object.keys(checkpoint.config_diff.removed).length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Removed</Label>
                  {Object.entries(checkpoint.config_diff.removed).map(([key, value]) => (
                    <div key={key} className="text-sm font-mono text-red-600 bg-muted p-2 rounded">
                      - {key}: {formatValue(value)}
                    </div>
                  ))}
                </div>
              )}

              {Object.keys(checkpoint.config_diff.added).length === 0 &&
               Object.keys(checkpoint.config_diff.removed).length === 0 &&
               Object.keys(checkpoint.config_diff.changed).length === 0 && (
                <p className="text-sm text-muted-foreground">No configuration changes</p>
              )}
            </CardContent>
          </Card>
        )}

        <Separator />

        {/* Review Actions */}
        {isPending ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Review Notes</Label>
              <Textarea
                value={reviewNotes}
                onChange={e => setReviewNotes(e.target.value)}
                placeholder="Add notes about your decision..."
                rows={3}
              />
            </div>

            {/* Feedback for next iteration - only when approving */}
            <div className="space-y-2">
              <Label>Feedback for Optimizer (optional)</Label>
              <Textarea
                value={feedback}
                onChange={e => setFeedback(e.target.value)}
                placeholder="Guidance for the next iteration... e.g., 'Focus more on reducing latency' or 'The error handling section is good, keep it'"
                rows={2}
                className="text-sm"
              />
              <p className="text-xs text-muted-foreground">
                This feedback will be sent to the optimizer for the next iteration only.
              </p>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch
                  id="publish"
                  checked={publishOnApprove}
                  onCheckedChange={setPublishOnApprove}
                />
                <Label htmlFor="publish" className="text-sm">
                  Publish config on approve
                </Label>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  id="stop"
                  checked={stopOnReject}
                  onCheckedChange={setStopOnReject}
                />
                <Label htmlFor="stop" className="text-sm text-destructive">
                  Stop run on reject
                </Label>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">
            {checkpoint.reviewed_by && (
              <p>Reviewed by: {checkpoint.reviewed_by}</p>
            )}
            {checkpoint.reviewed_at && (
              <p>Reviewed at: {new Date(checkpoint.reviewed_at).toLocaleString()}</p>
            )}
            {checkpoint.review_notes && (
              <p className="mt-2 italic">"{checkpoint.review_notes}"</p>
            )}
          </div>
        )}

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={onClose}>
            {isPending ? 'Cancel' : 'Close'}
          </Button>

          {isPending && (
            <>
              <Button
                variant="destructive"
                onClick={handleReject}
                disabled={submitting}
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <X className="h-4 w-4 mr-2" />
                )}
                Reject
              </Button>

              <Button
                onClick={handleApprove}
                disabled={submitting}
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Check className="h-4 w-4 mr-2" />
                )}
                Approve & Continue
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CheckpointReview;
